import AuditFarmPicker, { farmGembas, farmZones } from "./audits/AuditFarmPicker";
import { BACKEND_URL } from "../api/config";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaChevronDown,
  FaClipboardCheck,
  FaClipboardList,
  FaFlask,
  FaLightbulb,
  FaProjectDiagram,
  FaSignOutAlt,
  FaTachometerAlt,
  FaTags,
  FaTools,
  FaUserCog,
} from "react-icons/fa";
import axios from "axios";
import MobileNavigation from "./navigation/MobileNavigation";
import KamishibaiAuditLinks from "./KamishibaiAuditLinks";

import "../styles/layout/navbar.css";
import "../styles/layout/mobile-navigation.css";

const API =
  BACKEND_URL;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const SprigIcon = ({
  className = "",
  size = 22,
}) => (
  <svg
    className={`logo-sprig ${className}`}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      className="sprig-stem"
      d="M4 20C8 16 13 9 21 4"
    />

    <path
      className="sprig-leaf"
      d="
        M4 20
        C4 20 3 11 9 6
        C15 1 21 4 21 4
        C21 4 22 12 16 17
        C10 22 4 20 4 20Z
      "
    />

    <path
      className="sprig-leaf sprig-leaf-2"
      d="
        M14 16
        C14 16 16 12 20 11
        C24 10 23 14 21 16
        C19 18 14 16 14 16Z
      "
      transform="translate(-2,-2)"
    />
  </svg>
);

const UserGlyph = ({ size = 20 }) => (
  <svg
    className="user-icon-svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="8"
      r="4"
      strokeWidth="1.6"
    />

    <path
      d="
        M4 20
        C4 16 7.5 14 12 14
        C16.5 14 20 16 20 20
      "
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const LeafToggle = () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg>;

const formatName = (value) =>
  value
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );

const AuditDrillPanel = ({
  gembas,
  sixsGembas,
  zones,
  sixsZones,
  canKamiSetup,
  isAdmin,
  onSelectZone,
  onExpandGemba,
  expandedGemba,
  expandedSixsGemba,
  go,
}) => {
  const [stage, setStage] = useState("type");
  const [auditType, setAuditType] = useState(null);
  const [auditFarm, setAuditFarm] = useState("");
  const [animationDirection, setAnimationDirection] =
    useState("forward");

  const goToGembas = useCallback((type) => {
    setAnimationDirection("forward");
    setAuditType(type);
    setAuditFarm("");
    setStage("gemba");
  }, []);

  const goBack = useCallback(() => {
    setAnimationDirection("back");
    setStage("type");
    setAuditType(null);
  }, []);

  const activeGembas = farmGembas(auditType === "sixs" ? sixsGembas : gembas, auditFarm);

  const activeZones = farmZones(auditType === "sixs" ? sixsZones : zones, auditFarm);

  const activeExpandedGemba =
    auditType === "sixs"
      ? expandedSixsGemba
      : expandedGemba;

  return (
    <div className="drill-panel">
      <div className="drill-crumb">
        {stage === "gemba" && (
          <button
            type="button"
            className="drill-back"
            onClick={goBack}
            aria-label="Back to audit types"
          >
            <FaArrowLeft />
          </button>
        )}

        <div className="drill-crumb-path">
          <span
            className={
              stage === "type"
                ? "dc-active"
                : "dc-link"
            }
            onClick={
              stage === "gemba"
                ? goBack
                : undefined
            }
            role={
              stage === "gemba"
                ? "button"
                : undefined
            }
            tabIndex={
              stage === "gemba"
                ? 0
                : undefined
            }
          >
            Audits
          </span>

          {stage === "gemba" && (
            <>
              <span className="dc-sep">›</span>

              <span className="dc-active">
                {auditType === "process"
                  ? "Process audit"
                  : "6S audit"}
              </span>
            </>
          )}
        </div>
      </div>

      {stage === "type" && (
        <div
          className={`drill-stage ${
            animationDirection === "back"
              ? "enter-from-left"
              : ""
          }`}
        >
          <div className="drill-type-list">
            <button
              type="button"
              className="drill-type-card"
              onClick={() =>
                goToGembas("process")
              }
            >
              <span className="dtc-icon dtc-icon-process">
                <FaClipboardList />
              </span>

              <span className="dtc-body">
                <span className="dtc-name">
                  Process audit
                </span>

                <span className="dtc-sub">
                  Operational workflow checks
                </span>
              </span>

              <span className="dtc-count dtc-count-process">
                {gembas.length}{" "}
                {gembas.length === 1
                  ? "gemba"
                  : "gembas"}
              </span>

              <FaChevronDown className="dtc-arrow" />
            </button>

            <button
              type="button"
              className="drill-type-card"
              onClick={() =>
                goToGembas("sixs")
              }
            >
              <span className="dtc-icon dtc-icon-sixs">
                <FaProjectDiagram />
              </span>

              <span className="dtc-body">
                <span className="dtc-name">
                  6S audit
                </span>

                <span className="dtc-sub">
                  Sort, set, shine and standardise
                </span>
              </span>

              <span className="dtc-count dtc-count-sixs">
                {sixsGembas.length}{" "}
                {sixsGembas.length === 1
                  ? "gemba"
                  : "gembas"}
              </span>

              <FaChevronDown className="dtc-arrow" />
            </button>

            <KamishibaiAuditLinks go={go} canSetup={canKamiSetup} />

            {isAdmin && (
              <div className="drill-shortcuts drill-shortcuts-admin">
                <button
                  type="button"
                  className="drill-shortcut audit-setup-shortcut"
                  onClick={() =>
                    go("/audits-setup")
                  }
                >
                  <span className="audit-setup-shortcut-icon"><FaUserCog /></span>
                  <span className="audit-setup-shortcut-copy"><strong>Audit Setup Studio</strong><small>Questions, Gembas & zone mapping</small></span>
                  <span className="pi-badge">Admin</span>
                  <FaChevronDown className="audit-setup-shortcut-arrow" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {stage === "gemba" && (
        <div className="drill-stage enter-from-right">
          <AuditFarmPicker gembas={auditType === "sixs" ? sixsGembas : gembas} kind={auditType} value={auditFarm} onChange={setAuditFarm} />
          <div className="drill-gemba-list">
            {activeGembas.map((gemba) => (
              <div
                key={gemba.id}
                className="drill-gemba-item"
              >
                <div
                  className={`drill-gemba-header ${
                    activeExpandedGemba === gemba.id
                      ? "open"
                      : ""
                  }`}
                  onClick={() =>
                    onExpandGemba(
                      gemba,
                      auditType === "sixs"
                    )
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      onExpandGemba(
                        gemba,
                        auditType === "sixs"
                      );
                    }
                  }}
                >
                  <span
                    className={`drill-gemba-dot ${
                      auditType === "sixs"
                        ? "dot-olive"
                        : "dot-rose"
                    }`}
                  />

                  <span className="drill-gemba-name">
                    {formatName(gemba.name)}
                  </span>

                  <span className="drill-gemba-zcount">
                    {(activeZones[gemba.id] || [])
                      .length > 0
                      ? `${
                          (
                            activeZones[gemba.id] ||
                            []
                          ).length
                        } zones`
                      : ""}
                  </span>

                  <FaChevronDown
                    className={`drill-gemba-chev ${
                      activeExpandedGemba ===
                      gemba.id
                        ? "open"
                        : ""
                    }`}
                  />
                </div>

                {activeExpandedGemba ===
                  gemba.id && (
                  <div className="drill-zones">
                    {(activeZones[gemba.id] || [])
                      .length > 0 ? (
                      (
                        activeZones[gemba.id] || []
                      ).map((zone) => (
                        <button
                          type="button"
                          key={zone.id}
                          className="drill-zone-chip"
                          onClick={() =>
                            onSelectZone(
                              zone,
                              gemba,
                              auditType
                            )
                          }
                        >
                          {formatName(zone.name)}
                        </button>
                      ))
                    ) : (
                      <span className="zone-empty">
                        Loading…
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {activeGembas.length === 0 && (
              <div className="drill-empty">
                Select a farm to see its mapped Gembas. If none appear, check zone farm assignments in setup.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ onLogout, permissions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  const token = localStorage.getItem("token");

  const [userData, setUserData] = useState(() => {
    try { return JSON.parse(localStorage.getItem("current_user")); }
    catch { return null; }
  });
  const [menuActive, setMenuActive] =
    useState(false);
  const [activeCluster, setActiveCluster] =
    useState(null);
  const [drillKey, setDrillKey] = useState(0);

  const [gembas, setGembas] = useState([]);
  const [sixsGembas, setSixsGembas] =
    useState([]);
  const [zones, setZones] = useState({});
  const [sixsZones, setSixsZones] =
    useState({});
  const [expandedGemba, setExpandedGemba] =
    useState(null);
  const [
    expandedSixsGemba,
    setExpandedSixsGemba,
  ] = useState(null);

  const closeAll = useCallback(() => {
    setMenuActive(false);
    setActiveCluster(null);
    setDrillKey((current) => current + 1);
    setExpandedGemba(null);
    setExpandedSixsGemba(null);
  }, []);

  const go = useCallback(
    (path) => {
      navigate(path);
      closeAll();
    },
    [navigate, closeAll]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        navRef.current &&
        !navRef.current.contains(event.target)
      ) {
        setActiveCluster(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
  }, []);

  useEffect(() => {
    setActiveCluster(null);
    setDrillKey((current) => current + 1);
    setMenuActive(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!token) return;

    // App.js already refreshes this profile in the background. Reuse the
    // login/bootstrap cache instead of making the same request twice.
    if (localStorage.getItem("current_user")) return;

    axios
      .get(`${API}/api/auth/users/me/`, {
        headers: authHeaders(token),
      })
      .then((response) => {
        setUserData(response.data);
        localStorage.setItem("current_user", JSON.stringify(response.data));
      })
      .catch(() => {
        localStorage.clear();
        navigate("/login");
      });
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    Promise.all([
      axios.get(`${API}/api/audits/gembas/`, {
        headers: authHeaders(token),
      }),

      axios.get(`${API}/api/sixs/gembas/`, {
        headers: authHeaders(token),
      }),
    ])
      .then(
        ([
          processResponse,
          sixsResponse,
        ]) => {
          setGembas(
            Array.isArray(processResponse.data)
              ? processResponse.data
              : processResponse.data?.results || []
          );

          setSixsGembas(
            Array.isArray(sixsResponse.data)
              ? sixsResponse.data
              : sixsResponse.data?.results || []
          );
        }
      )
      .catch((error) => {
        console.error(
          "Navbar gemba fetch error:",
          error
        );
      });
  }, [token]);

  const toggleCluster = useCallback((name) => {
    setActiveCluster((current) => {
      const next =
        current === name ? null : name;

      if (next === "audit") {
        setDrillKey(
          (currentKey) => currentKey + 1
        );
      }

      return next;
    });
  }, []);

  const handleClusterEnter =
    useCallback((name) => {
      if (window.innerWidth <= 1600) return;

      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }

      setActiveCluster(name);
    }, []);

  const handleClusterLeave = useCallback(() => {
    if (window.innerWidth <= 1600) return;

    closeTimer.current = setTimeout(() => {
      setActiveCluster(null);
    }, 140);
  }, []);

  const handleExpandGemba = useCallback(
    async (gemba, isSixs) => {
      const currentExpanded = isSixs
        ? expandedSixsGemba
        : expandedGemba;

      const setExpanded = isSixs
        ? setExpandedSixsGemba
        : setExpandedGemba;

      const zoneCollection = isSixs
        ? sixsZones
        : zones;

      const setZoneCollection = isSixs
        ? setSixsZones
        : setZones;

      const endpointPrefix = isSixs
        ? "sixs"
        : "audits";

      if (currentExpanded === gemba.id) {
        setExpanded(null);
        return;
      }

      setExpanded(gemba.id);

      if (zoneCollection[gemba.id]) {
        return;
      }

      try {
        const response = await axios.get(
          `${API}/api/${endpointPrefix}/gembas/${gemba.id}/zones/`,
          {
            headers: authHeaders(token),
          }
        );

        const responseZones =
          Array.isArray(response.data)
            ? response.data
            : response.data?.zones ||
              response.data?.results ||
              [];

        setZoneCollection((current) => ({
          ...current,
          [gemba.id]: responseZones,
        }));
      } catch (error) {
        console.error(
          `${
            isSixs ? "6S" : "Process"
          } zones error:`,
          error
        );

        setZoneCollection((current) => ({
          ...current,
          [gemba.id]: [],
        }));
      }
    },
    [
      token,
      expandedGemba,
      expandedSixsGemba,
      zones,
      sixsZones,
    ]
  );

  const handleSelectZone = useCallback(
    (zone, gemba, type) => {
      localStorage.setItem(
        "selectedGemba",
        JSON.stringify(gemba)
      );

      localStorage.setItem(
        "selectedZone",
        JSON.stringify(zone)
      );

      navigate(
        type === "sixs"
          ? `/sixs-audit/${gemba.id}?zone=${zone.id}&farm=${zone.farm}`
          : `/audit-forms/${gemba.id}?zone=${zone.id}&farm=${zone.farm}`
      );

      closeAll();
    },
    [navigate, closeAll]
  );

  const handleLogout = useCallback(() => {
    onLogout?.();
    navigate("/login", { replace: true });
  }, [navigate, onLogout]);

  const can = (code) => Boolean((permissions || userData?.permissions)?.["*"] || (permissions || userData?.permissions)?.[code]);
  const isAdmin = can("audits.setup");

  const isManager = can("admin.access_control");

  const isEsg = can("compliance.setup");

  const displayName =
    userData?.first_name ||
    userData?.username ||
    "User";

  const clusters = [
    {
      id: "audit",
      theme: "audit",
      icon: <FaClipboardList />,
      label: "Audits",
      title: "Audit Management",
      subtitle:
        "Process, 6S and Kamishibai audits",
    },
    {
      id: "tools",
      theme: "tools",
      icon: <FaTools />,
      label: "Kaizen Kit",
      title: "Kaizen Kit",
      subtitle:
        "LCM and continuous improvement",
    },
    {
      id: "lab",
      theme: "lab",
      icon: <FaFlask />,
      label: "TukuzaLab",
      title: "TukuzaLab",
      subtitle:
        "Experiments and innovation hub",
    },
    {
      id: "compliance",
      theme: "compliance",
      icon: <FaClipboardCheck />,
      label: "Compliance",
      title: "Compliance",
      subtitle:
        "Clause management and responses",
    },
    {
      id: "scheduler",
      theme: "tools",
      icon: <FaCalendarAlt />,
      label: "Scheduler",
      title: "Team Scheduler",
      subtitle:
        "Task management and coordination",
    },
  ];

  const quickActions = [
    ...(can("gemba_performance.weights") && !isManager ? [{path:"/admin/gemba-performance",icon:<FaUserCog />,label:"Scoring settings",active:location.pathname==="/admin/gemba-performance"}] : []),
    ...(can("audit_drafts.view") ? [{path: "/audit-drafts", icon: <FaTags />, label: "Audit Drafts", active: location.pathname === "/audit-drafts"}] : []),
    ...(["process", "sixs", "kamishibai"].some(k => ["view", "create", "update", "delete"].some(a => can(`${k}_submissions.${a}`))) ? [{path: "/audit-submissions", icon: <FaTags />, label: "Submissions", active: location.pathname === "/audit-submissions"}] : []),
    {
      path: "/dashboard",
      icon: <FaTachometerAlt />,
      label: "Dashboard",
      active:
        location.pathname === "/dashboard",
    },
    {
      path: "/nac-reconciliation",
      icon: <FaTags />,
      label: "Tags",
      active:
        location.pathname ===
        "/nac-reconciliation",
    },
    ...(isManager
      ? [
          {
            path: "/admin",
            icon: <FaUserCog />,
            label: "Admin",
            active:
              location.pathname === "/admin",
          },
        ]
      : []),
  ];

  const mobileGroups = [
    { id: "overview", label: "Overview", icon: <FaTachometerAlt />, description: "Your everyday starting points.", links: [
      { path: "/welcome", label: "Home", description: "Your Tukuza workspace" },
      { path: "/dashboard", label: "Dashboard", description: "See progress across your team" },
      { path: "/nac-reconciliation", label: "Tags reconciliation", description: "Follow up and close the loop", icon: <FaTags /> },
      ...quickActions.filter(item => !["/dashboard", "/nac-reconciliation"].includes(item.path)).map(item => ({ ...item, description: item.label === "Admin" ? "Manage people and access" : "Review and manage your records" })),
    ] },
    { id: "audits", label: "Audits", icon: <FaClipboardList />, description: "Choose an audit, work area and zone." },
    { id: "kaizen", label: "Kaizen Kit", icon: <FaLightbulb />, description: "Tools for a better working day.", links: [
      { path: "/daily-kaizen", label: "Daily Kaizen", description: "Work plans, teams and daily improvement" },
      { path: "/trainings", label: "Trainings", description: "Plan learning, track completion and view training sheets", icon: <FaTools /> },
      { path: "/skills-matrix", label: "Skills matrix", description: "Explore capabilities and learning", icon: <FaTools /> },
      { path: "/lcm/lcmmarks", label: "My rating", description: "Record your LCM assessment" },
      { path: "/lcm/lcmdashboard", label: "LCM dashboard", description: "See your performance picture" },
      ...(isAdmin ? [{ path: "/lcm/lcmsetup", label: "LCM setup", description: "Configure assessments" }] : []),
    ] },
    { id: "lab", label: "TukuzaLab", icon: <FaFlask />, description: "Make room for your next good idea.", links: [
      { path: "/tukuzalab/tukuzalabdashboard", label: "Lab dashboard", description: "Follow ideas from spark to impact" },
      ...((permissions["*"] || permissions["visitor_feedback.view"] === "all") ? [{ path: "/tukuzalab/visitor-feedback", label: "Visitor feedback", description: "Explore visitor experiences and Kaizen insights" }] : []),
      { path: "/tukuzalab/tukuzalabsubmissions", label: "Ideas & visitor feedback", description: "Share an idea or reflect on your visit" },
      ...(isAdmin ? [{ path: "/tukuzalab/tukuzalabsetup", label: "Lab setup", description: "Manage the innovation programme" }] : []),
    ] },
    { id: "compliance", label: "Compliance", icon: <FaClipboardCheck />, description: "Keep standards visible and actionable.", links: [
      { path: "/compliance/clause-response-dashboard", label: "Clause dashboard", description: "Track compliance progress" },
      { path: "/compliance/clause-responses", label: "Clause responses", description: "Review and complete checks" },
      ...(isEsg ? [{ path: "/compliance/clause-setup", label: "Clause setup", description: "Maintain your standards" }] : []),
    ] },
    { id: "scheduler", label: "Scheduler", icon: <FaCalendarAlt />, description: "The right work, at the right time.", links: [
      { path: "/scheduler/team-dashboard", label: "Team dashboard", description: "See what is planned" },
      { path: "/scheduler/team-tasks", label: "Team tasks", description: "Organize the work ahead" },
      ...(isAdmin ? [{ path: "/scheduler/team-members-setup", label: "Team setup", description: "Manage teams and members" }] : []),
    ] },
  ];

  return (
    <>
      <nav
        className="navbar"
        ref={navRef}
      >
        <div className="nav-left">
          <button
            type="button"
            className={`leaf-toggle ${
              menuActive ? "open" : ""
            }`}
            onClick={() =>
              setMenuActive(
                (current) => !current
              )
            }
            aria-label={
              menuActive
                ? "Close menu"
                : "Open menu"
            }
            aria-controls="workspace-navigation"
            aria-expanded={menuActive}
          >
            <LeafToggle /><span className="menu-button-label">Menu</span>
          </button>

          <div className="nav-logo">
            <SprigIcon size={22} />
            TMS
            <span className="logo-sub">
              Kaizen
            </span>
          </div>
        </div>

        <div className="capsule-ribbon">
          <div className="capsule-island">
            {clusters.map((cluster) => (
              <div
                key={cluster.id}
                className={`leaf-cluster ${
                  activeCluster === cluster.id
                    ? "active"
                    : ""
                }`}
                data-theme={cluster.theme}
                onMouseEnter={() =>
                  handleClusterEnter(cluster.id)
                }
                onMouseLeave={
                  handleClusterLeave
                }
              >
                <button
                  type="button"
                  className="cluster-trigger"
                  aria-label={cluster.label}
                  title={cluster.label}
                  onClick={() =>
                    toggleCluster(cluster.id)
                  }
                  aria-expanded={
                    activeCluster === cluster.id
                  }
                  aria-haspopup="true"
                >
                  <span className="ct-icon">
                    {cluster.icon}
                  </span>

                  <span className="ct-label">
                    {cluster.label}
                  </span>

                  <FaChevronDown className="ct-chevron" />
                </button>

                <div className="cluster-panel">
                  <div className="panel-header">
                    <div
                      className={`panel-header-icon ph-${cluster.theme}`}
                    >
                      {cluster.icon}
                    </div>

                    <div>
                      <h4>{cluster.title}</h4>
                      <p>{cluster.subtitle}</p>
                    </div>
                  </div>

                  {cluster.id === "audit" && (
                    <AuditDrillPanel
                      key={drillKey}
                      gembas={gembas}
                      sixsGembas={sixsGembas}
                      zones={zones}
                      sixsZones={sixsZones}
                      canKamiSetup={can("kamishibai.setup") || can("kamishibai.mapping")}
                      isAdmin={isAdmin}
                      onSelectZone={
                        handleSelectZone
                      }
                      onExpandGemba={
                        handleExpandGemba
                      }
                      expandedGemba={
                        expandedGemba
                      }
                      expandedSixsGemba={
                        expandedSixsGemba
                      }
                      go={go}
                    />
                  )}

                  {cluster.id === "tools" && (
                    <div className="panel-body">
                      <div className="panel-section">
                        LCM
                      </div>

                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go("/lcm/lcmmarks")
                        }
                      >
                        <span className="pi-dot dot-olive" />
                        <span className="pi-label">
                          My Rating
                        </span>
                      </button>

                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go("/lcm/lcmdashboard")
                        }
                      >
                        <span className="pi-dot dot-olive" />
                        <span className="pi-label">
                          LCM Dashboard
                        </span>
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          className="panel-item full-row"
                          onClick={() =>
                            go("/lcm/lcmsetup")
                          }
                        >
                          <span className="pi-dot dot-muted" />
                          <span className="pi-label">
                            LCM Setup
                          </span>
                          <span className="pi-badge">
                            Admin
                          </span>
                        </button>
                      )}

                      <div className="panel-section">
                        Kaizen
                      </div>

                      <div className="kaizen-group">
                        <div className="kaizen-sub">
                          <div className="kaizen-sub-header">
                            <span className="kh-icon kh-daily">
                              <FaLightbulb />
                            </span>

                            <span className="kh-label">
                              Daily Kaizen
                            </span>
                          </div>

                          <div className="kaizen-items">
                            <button type="button" className="panel-item" onClick={() => go("/trainings")}><span className="pi-dot dot-olive" /><span className="pi-label">Trainings</span></button>
                            <button
                              type="button"
                              className="panel-item"
                              onClick={() =>
                                go("/daily-kaizen")
                              }
                            >
                              <span className="pi-dot dot-rose" />
                              <span className="pi-label">
                                Daily Kaizen
                              </span>
                            </button>

                            <button
                              type="button"
                              className="panel-item"
                              onClick={() =>
                                go("/skills-matrix")
                              }
                            >
                              <span className="pi-dot dot-olive" />
                              <span className="pi-label">
                                Skills Matrix
                              </span>
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}

                  {cluster.id === "lab" && (
                    <div className="panel-body single-col">
                      {(permissions["*"] || permissions["visitor_feedback.view"] === "all") && <button type="button" className="panel-item" onClick={() => go("/tukuzalab/visitor-feedback")}><span className="pi-dot dot-olive"/><span className="pi-label">Visitor feedback</span></button>}
                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/tukuzalab/tukuzalabdashboard"
                          )
                        }
                      >
                        <span className="pi-dot dot-green" />
                        <span className="pi-label">
                          Lab Dashboard
                        </span>
                      </button>

                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/tukuzalab/tukuzalabsubmissions"
                          )
                        }
                      >
                        <span className="pi-dot dot-green" />
                        <span className="pi-label">
                          Submissions
                        </span>
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          className="panel-item"
                          onClick={() =>
                            go(
                              "/tukuzalab/tukuzalabsetup"
                            )
                          }
                        >
                          <span className="pi-dot dot-muted" />
                          <span className="pi-label">
                            Lab Setup
                          </span>
                          <span className="pi-badge">
                            Admin
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {cluster.id ===
                    "compliance" && (
                    <div className="panel-body single-col">
                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/compliance/clause-response-dashboard"
                          )
                        }
                      >
                        <span className="pi-dot dot-purple" />
                        <span className="pi-label">
                          Clause Dashboard
                        </span>
                      </button>

                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/compliance/clause-responses"
                          )
                        }
                      >
                        <span className="pi-dot dot-purple" />
                        <span className="pi-label">
                          Clause Responses
                        </span>
                      </button>

                      {isEsg && (
                        <button
                          type="button"
                          className="panel-item"
                          onClick={() =>
                            go(
                              "/compliance/clause-setup"
                            )
                          }
                        >
                          <span className="pi-dot dot-muted" />
                          <span className="pi-label">
                            Clause Setup
                          </span>
                          <span className="pi-badge">
                            ESG
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {cluster.id ===
                    "scheduler" && (
                    <div className="panel-body single-col">
                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/scheduler/team-dashboard"
                          )
                        }
                      >
                        <span className="pi-dot dot-olive" />
                        <span className="pi-label">
                          Team Dashboard
                        </span>
                      </button>

                      <button
                        type="button"
                        className="panel-item"
                        onClick={() =>
                          go(
                            "/scheduler/team-tasks"
                          )
                        }
                      >
                        <span className="pi-dot dot-olive" />
                        <span className="pi-label">
                          Team Tasks
                        </span>
                      </button>

                      {isAdmin && (
                        <button
                          type="button"
                          className="panel-item"
                          onClick={() =>
                            go(
                              "/scheduler/team-members-setup"
                            )
                          }
                        >
                          <span className="pi-dot dot-muted" />
                          <span className="pi-label">
                            Team &amp; Members Setup
                          </span>
                          <span className="pi-badge">
                            Admin
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <span
              className="capsule-stem"
              aria-hidden="true"
            />

            <div className="ribbon-quick">
              {quickActions.map((action) => (
                <button
                  type="button"
                  key={action.path}
                  aria-label={action.label}
                  title={action.label}
                  className={`ribbon-quick-btn ${
                    action.active ? "active" : ""
                  }`}
                  onClick={() =>
                    go(action.path)
                  }
                >
                  <span className="qb-icon">
                    {action.icon}
                  </span>

                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="compact-navigation"><span className="compact-navigation__label">Your workspace</span><div><button onClick={() => go("/dashboard")} aria-current={location.pathname === "/dashboard" ? "page" : undefined}><FaTachometerAlt /> Overview</button><button onClick={() => go("/daily-kaizen")} aria-current={location.pathname === "/daily-kaizen" ? "page" : undefined}><FaLightbulb /> Daily Kaizen</button></div></div>

        <div className="navbar-right">
          <button className="compact-account" onClick={() => setMenuActive(true)} aria-label={`Open workspace menu for ${displayName}`} aria-expanded={menuActive} aria-controls="workspace-navigation"><span>{displayName.slice(0, 1).toUpperCase()}</span><span className="compact-account__name">{displayName}</span><FaChevronDown /></button>
          <div className="module-pill">
            <div className="pill-user">
              <UserGlyph size={20} />

              <span className="welcome-text">
                <span className="label-prefix">
                  Welcome
                </span>

                <strong>{displayName}</strong>
              </span>
            </div>

            <span className="pill-divider" />

            <button
              type="button"
              className="pill-logout"
              onClick={handleLogout}
            >
              <FaSignOutAlt />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {menuActive && <MobileNavigation groups={mobileGroups} displayName={displayName} role={formatName(userData?.role)} pathname={location.pathname} go={go} onClose={closeAll} onLogout={handleLogout} auditPanel={<AuditDrillPanel key={drillKey} gembas={gembas} sixsGembas={sixsGembas} zones={zones} sixsZones={sixsZones} canKamiSetup={can("kamishibai.setup") || can("kamishibai.mapping")} isAdmin={isAdmin} onSelectZone={handleSelectZone} onExpandGemba={handleExpandGemba} expandedGemba={expandedGemba} expandedSixsGemba={expandedSixsGemba} go={go} />} />}
    </>
  );
};

export default Navbar;
